import {
  AttentionBlock,
  Button,
  Field,
  PickList,
  Skeleton,
  Textarea,
  formatRelative,
  toast,
  type PickListItem,
} from '@westhetics/ui'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { LoadError } from '@/app/load-error'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { routes } from '@/routes'
import {
  useCatalogOptions,
  useCatalogRequests,
  useResolveCatalogRequest,
  type CatalogRequest,
} from './api'

/**
 * Pedidos de inclusão das clínicas, no topo do catálogo (docs/areas/administracao.md, RN-ADM-75).
 * A lista mestre é fechada: quem não acha o procedimento pede, e a Westhetics responde aqui,
 * incluindo (com um item que já existe ou criando um) ou recusando com o motivo.
 */

const NOTE_MESSAGE = 'Diga à clínica por que o procedimento não entra, em 10 a 500 caracteres.'

type Mode = 'include' | 'decline'

export function CatalogRequestsBlock({ className }: { className?: string }) {
  const requests = useCatalogRequests('open')
  const [open, setOpen] = useState<{ id: string; mode: Mode } | null>(null)

  if (requests.isError) {
    return (
      <LoadError
        className={className}
        title="Não conseguimos carregar os pedidos das clínicas"
        error={requests.error}
        onRetry={() => void requests.refetch()}
      />
    )
  }
  if (requests.isPending) {
    return (
      <div role="status" aria-label="Carregando os pedidos das clínicas" className={className}>
        <Skeleton className="h-[92px] w-full" />
      </div>
    )
  }
  const items = requests.data.data
  if (items.length === 0) return null

  const toggle = (id: string, mode: Mode) =>
    setOpen((current) => (current?.id === id && current.mode === mode ? null : { id, mode }))

  return (
    <AttentionBlock variant="lilac" className={className} title={blockTitle(items)}>
      <ul>
        {items.map((request) => {
          const mode = open?.id === request.id ? open.mode : null
          return (
            <li key={request.id} className="border-hairline border-t py-2 first:border-t-0">
              <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                <div className="min-w-0">
                  <div>
                    {request.name}
                    <span className="text-secondary">
                      {' '}
                      · {request.clinic.name} · {formatRelative(new Date(request.createdAt))}
                    </span>
                  </div>
                  {request.note ? (
                    <p className="text-secondary text-[12px] leading-[1.6]">"{request.note}"</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-baseline gap-4">
                  <Button
                    variant="text"
                    size="sm"
                    aria-expanded={mode === 'include'}
                    onClick={() => toggle(request.id, 'include')}
                  >
                    incluir
                  </Button>
                  <Button
                    variant="text"
                    size="sm"
                    aria-expanded={mode === 'decline'}
                    onClick={() => toggle(request.id, 'decline')}
                  >
                    recusar
                  </Button>
                </div>
              </div>
              {mode === 'include' ? (
                <IncludePanel request={request} onClose={() => setOpen(null)} />
              ) : null}
              {mode === 'decline' ? (
                <DeclinePanel request={request} onClose={() => setOpen(null)} />
              ) : null}
            </li>
          )
        })}
      </ul>
    </AttentionBlock>
  )
}

/** "2 clínicas pediram procedimentos que não estão no catálogo", com o singular certo. */
function blockTitle(requests: CatalogRequest[]) {
  const clinicCount = new Set(requests.map((request) => request.clinic.id)).size
  if (clinicCount > 1) {
    return `${clinicCount} clínicas pediram procedimentos que não estão no catálogo`
  }
  return requests.length > 1
    ? '1 clínica pediu procedimentos que não estão no catálogo'
    : '1 clínica pediu um procedimento que não está no catálogo'
}

/** Incluir: atender o pedido com um item que já está na lista mestre, ou criar o item. */
function IncludePanel({ request, onClose }: { request: CatalogRequest; onClose: () => void }) {
  const options = useCatalogOptions()
  const resolve = useResolveCatalogRequest()
  const [chosen, setChosen] = useState<string[]>([])

  const items = useMemo<PickListItem[]>(
    () =>
      (options.data ?? []).map((option) => ({
        id: option.id,
        label: option.name,
        group: option.groupName,
        keywords: option.aliases,
        note: option.hasConsentForm ? undefined : 'sem termo publicado',
      })),
    [options.data],
  )
  const chosenOption = options.data?.find((option) => option.id === chosen[0])
  const createHref = `${routes.admin.newCatalogProcedure}?${new URLSearchParams({
    name: request.name,
    request: request.id,
  }).toString()}`

  return (
    <div className="mt-2 mb-2 flex flex-col gap-3">
      {options.isError ? (
        <LoadError
          title="Não conseguimos carregar o catálogo para escolher"
          error={options.error}
          onRetry={() => void options.refetch()}
        />
      ) : options.isPending ? (
        <div role="status" aria-label="Carregando o catálogo" className="flex flex-col gap-2">
          <Skeleton className="h-[40px] w-full" />
          <Skeleton className="h-[160px] w-full" />
        </div>
      ) : (
        <PickList
          label={`Procedimentos do catálogo para atender "${request.name}"`}
          items={items}
          value={chosen}
          multiple={false}
          autoFocus
          className="max-w-reading"
          searchPlaceholder="Buscar no catálogo"
          onChange={(ids) => {
            setChosen(ids)
            resolve.reset()
          }}
          emptySearch={(query) => (
            <>
              Nenhum procedimento do catálogo para "{query}". A busca cobre nome, sinônimos e grupo.
              Se ele ainda não existe, use "criar e incluir".
            </>
          )}
        />
      )}
      {resolve.isError ? (
        <p role="alert" className="font-body text-eyebrow text-plum leading-[1.5] font-light">
          {errorMessage(resolve.error)}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <Button
          variant="outline"
          size="sm"
          disabled={!chosenOption}
          loading={resolve.isPending}
          loadingLabel="Incluindo…"
          onClick={() => {
            if (!chosenOption) return
            resolve.mutate(
              { id: request.id, outcome: 'added', catalogProcedureId: chosenOption.id },
              {
                onSuccess: () => {
                  onClose()
                  toast(
                    `Pedido de "${request.name}" atendido com "${chosenOption.name}" — ${request.clinic.name} já pode adicionar`,
                  )
                },
              },
            )
          }}
        >
          {chosenOption
            ? `Atender com "${chosenOption.name}"`
            : 'Escolha um procedimento do catálogo'}
        </Button>
        <Button variant="text" size="sm" asChild>
          <Link to={createHref}>criar e incluir</Link>
        </Button>
        <Button variant="text" size="sm" onClick={onClose}>
          cancelar
        </Button>
      </div>
    </div>
  )
}

/** Recusar: o motivo é obrigatório, porque é o que a gestora lê no pedido dela. */
function DeclinePanel({ request, onClose }: { request: CatalogRequest; onClose: () => void }) {
  const resolve = useResolveCatalogRequest()
  const [note, setNote] = useState('')
  const [touched, setTouched] = useState(false)

  const length = note.trim().length
  const isValid = length >= 10 && length <= 500
  const serverError = resolve.isError
    ? (fieldErrors(resolve.error).resolutionNote ?? errorMessage(resolve.error))
    : null
  const error = touched && !isValid ? NOTE_MESSAGE : serverError

  function submit() {
    setTouched(true)
    if (!isValid) return
    resolve.mutate(
      { id: request.id, outcome: 'declined', resolutionNote: note.trim() },
      {
        onSuccess: () => {
          onClose()
          toast(`Pedido de "${request.name}" recusado — ${request.clinic.name} lê o motivo`)
        },
      },
    )
  }

  return (
    <div className="mt-2 mb-2 flex flex-col gap-3">
      <Field
        className="max-w-reading"
        label="Motivo da recusa"
        meta={`${length}/500`}
        error={error}
        hint="A gestora da clínica lê este texto no pedido dela."
      >
        <Textarea
          autoFocus
          value={note}
          maxLength={500}
          onChange={(event) => {
            setNote(event.target.value)
            if (resolve.isError) resolve.reset()
          }}
          onBlur={() => {
            if (note.trim() !== '') setTouched(true)
          }}
        />
      </Field>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <Button
          variant="outline"
          size="sm"
          loading={resolve.isPending}
          loadingLabel="Recusando…"
          onClick={submit}
        >
          Recusar pedido
        </Button>
        <Button variant="text" size="sm" onClick={onClose}>
          cancelar
        </Button>
      </div>
    </div>
  )
}
